import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import {
  AppShellLayout,
  AuthLayout,
  DefaultPageLayout,
  PlatformLayout,
  RootLayout,
  SalesforceOrgHubLayout,
  SettingsLayout,
  TelephonyConnectionHubLayout,
} from '@/layouts';
import {
  RequireAuth,
  RedirectIfAuthenticated,
  RequireTenant,
  RequirePlatformAdmin,
  RequirePermission,
} from '@/routes/guards';
import { RouteSuspense } from '@/routes/RouteSuspense';
import * as Pages from '@/routes/lazy-pages';

function suspense(element: ReactNode) {
  return <RouteSuspense>{element}</RouteSuspense>;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: suspense(<Pages.RouteErrorPage />),
    children: [
      // ── Public auth routes ──────────────────────────────────────────
      {
        element: <RedirectIfAuthenticated />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              { path: '/login', element: suspense(<Pages.LoginPage />) },
              { path: '/register', element: suspense(<Pages.RegisterPage />) },
              {
                path: '/forgot-password',
                element: suspense(<Pages.ForgotPasswordPage />),
              },
              { path: '/invite/:token', element: suspense(<Pages.InviteAcceptPage />) },
            ],
          },
        ],
      },

      // ── Authenticated routes ──────────────────────────────────────
      {
        element: <RequireAuth />,
        children: [
          { path: '/forbidden', element: suspense(<Pages.ForbiddenPage />) },

          // Legacy wizard URLs → Setup (configure landing)
          {
            path: '/setup',
            element: <Navigate to="/configure" replace />,
          },
          {
            path: '/setup/:step',
            element: <Navigate to="/configure" replace />,
          },

          // Tenant-scoped app (+ platform plane nested for shared auth)
          {
            element: <RequireTenant />,
            children: [
              {
                element: <AppShellLayout />,
                children: [
                  { index: true, element: <Navigate to="/dashboard" replace /> },
                  {
                    element: <RequirePermission permission="dashboard:view" />,
                    children: [
                      { path: '/dashboard', element: suspense(<Pages.DashboardPage />) },
                      {
                        path: '/configure',
                        element: <DefaultPageLayout />,
                        children: [
                          { index: true, element: suspense(<Pages.ConfigurePage />) },
                        ],
                      },
                    ],
                  },

                  // Salesforce
                  {
                    element: <RequirePermission permission="salesforce:view" />,
                    children: [
                      {
                        path: '/salesforce',
                        element: <DefaultPageLayout />,
                        children: [
                          {
                            index: true,
                            element: suspense(<Pages.SalesforceOrgListPage />),
                          },
                          {
                            path: ':orgId',
                            element: <SalesforceOrgHubLayout />,
                            children: [
                              { index: true, element: <Navigate to="overview" replace /> },
                              {
                                path: 'overview',
                                element: suspense(<Pages.OrgOverviewTab />),
                              },
                              {
                                path: 'authentication',
                                element: suspense(<Pages.OrgAuthenticationTab />),
                              },
                              {
                                path: 'routing',
                                element: suspense(<Pages.OrgRoutingTab />),
                              },
                              { path: 'queues', element: <Navigate to="../routing" replace /> },
                              {
                                path: 'voice-flows',
                                element: <Navigate to="../routing" replace />,
                              },
                              {
                                path: 'telephony',
                                element: suspense(<Pages.OrgTelephonyTab />),
                              },
                              { path: 'agents', element: suspense(<Pages.OrgAgentsTab />) },
                              {
                                path: 'phone-numbers',
                                element: <Navigate to="/telephony" replace />,
                              },
                              {
                                path: 'calls',
                                element: <Navigate to="../overview" replace />,
                              },
                              { path: 'audit', element: <Navigate to="/audit" replace /> },
                              { path: 'health', element: <Navigate to="/health" replace /> },
                              {
                                path: 'validation',
                                element: <Navigate to="/validation" replace />,
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },

                  // Telephony
                  {
                    element: <RequirePermission permission="telephony:view" />,
                    children: [
                      {
                        path: '/telephony',
                        element: <DefaultPageLayout />,
                        children: [
                          {
                            index: true,
                            element: suspense(<Pages.TelephonyConnectionListPage />),
                          },
                          {
                            path: 'phone-numbers',
                            element: suspense(<Pages.PhoneNumberListPage />),
                          },
                          {
                            path: ':connectionId',
                            element: <TelephonyConnectionHubLayout />,
                            children: [
                              { index: true, element: <Navigate to="overview" replace /> },
                              {
                                path: 'overview',
                                element: suspense(<Pages.ConnectionOverviewTab />),
                              },
                              {
                                path: 'config',
                                element: suspense(<Pages.ConnectionConfigTab />),
                              },
                              {
                                path: 'numbers',
                                element: suspense(<Pages.ConnectionNumbersTab />),
                              },
                              {
                                path: 'webhooks',
                                element: suspense(<Pages.ConnectionWebhooksTab />),
                              },
                              {
                                path: 'salesforce',
                                element: <Navigate to="overview" replace />,
                              },
                              { path: 'health', element: <Navigate to="/health" replace /> },
                              {
                                path: 'validation',
                                element: <Navigate to="/validation" replace />,
                              },
                              { path: 'audit', element: <Navigate to="/audit" replace /> },
                            ],
                          },
                        ],
                      },
                    ],
                  },

                  // Legacy Salesforce Agents list — redirect to Salesforce Orgs (agents live in org hub)
                  {
                    path: '/agents',
                    element: <Navigate to="/salesforce" replace />,
                  },
                  {
                    path: '/agents/:agentId',
                    element: <Navigate to="/salesforce" replace />,
                  },

                  // Operations
                  {
                    element: <RequirePermission permission="validation:view" />,
                    children: [
                      {
                        path: '/validation',
                        element: <DefaultPageLayout />,
                        children: [
                          {
                            index: true,
                            element: suspense(<Pages.ValidationCenterPage />),
                          },
                        ],
                      },
                    ],
                  },
                  // /health kept for deep links but omitted from navigation (placeholder)
                  {
                    element: <RequirePermission permission="health:view" />,
                    children: [
                      {
                        path: '/health',
                        element: <DefaultPageLayout />,
                        children: [
                          { index: true, element: suspense(<Pages.HealthCenterPage />) },
                        ],
                      },
                    ],
                  },
                  {
                    element: <RequirePermission permission="calls:view" />,
                    children: [
                      {
                        path: '/calls',
                        element: <DefaultPageLayout />,
                        children: [
                          { index: true, element: suspense(<Pages.CallListPage />) },
                          {
                            path: ':callId',
                            element: suspense(<Pages.CallDetailPage />),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    element: <RequirePermission permission="login_history:view" />,
                    children: [
                      {
                        path: '/login-history',
                        element: <DefaultPageLayout />,
                        children: [
                          { index: true, element: suspense(<Pages.LoginHistoryPage />) },
                        ],
                      },
                    ],
                  },
                  {
                    element: <RequirePermission permission="audit:view" />,
                    children: [
                      {
                        path: '/audit',
                        element: <DefaultPageLayout />,
                        children: [
                          { index: true, element: suspense(<Pages.AuditLogPage />) },
                        ],
                      },
                    ],
                  },

                  // Portal users
                  {
                    element: <RequirePermission permission="users:view" />,
                    children: [
                      {
                        path: '/users',
                        element: <DefaultPageLayout />,
                        children: [
                          {
                            index: true,
                            element: suspense(<Pages.PortalUserListPage />),
                          },
                          {
                            path: ':userId',
                            element: suspense(<Pages.PortalUserDetailPage />),
                          },
                        ],
                      },
                    ],
                  },

                  // Settings
                  {
                    path: '/settings',
                    element: <DefaultPageLayout />,
                    children: [
                      { index: true, element: <Navigate to="account" replace /> },
                      {
                        element: <SettingsLayout />,
                        children: [
                          {
                            path: 'account',
                            element: suspense(<Pages.SettingsAccountPage />),
                          },
                          {
                            path: 'tenant',
                            element: <Navigate to="/settings/organization" replace />,
                          },
                          {
                            path: 'organization',
                            element: suspense(<Pages.SettingsOrganizationPage />),
                          },
                          {
                            path: 'security',
                            element: suspense(<Pages.SettingsSecurityPage />),
                          },
                          {
                            path: 'credentials',
                            element: suspense(<Pages.SettingsCredentialsPage />),
                          },
                          {
                            path: 'credentials/:credentialId',
                            element: suspense(<Pages.CredentialDetailPage />),
                          },
                          {
                            path: 'notifications',
                            element: suspense(<Pages.SettingsNotificationsPage />),
                          },
                          {
                            path: 'general',
                            element: <Navigate to="/settings/organization" replace />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },

              // Platform admin plane
              {
                element: <RequirePlatformAdmin />,
                children: [
                  {
                    element: <PlatformLayout />,
                    children: [
                      {
                        path: '/platform',
                        element: <Navigate to="/platform/dashboard" replace />,
                      },
                      {
                        path: '/platform/dashboard',
                        element: suspense(<Pages.PlatformDashboardPage />),
                      },
                      {
                        path: '/platform/tenants',
                        element: suspense(<Pages.PlatformTenantListPage />),
                      },
                      {
                        path: '/platform/tenants/:tenantId',
                        element: suspense(<Pages.PlatformTenantDetailPage />),
                      },
                      {
                        path: '/platform/inventory',
                        element: suspense(<Pages.PlatformInventoryPage />),
                      },
                      {
                        path: '/platform/providers',
                        element: suspense(<Pages.PlatformProviderCatalogPage />),
                      },
                      {
                        path: '/platform/health',
                        element: suspense(<Pages.PlatformSystemHealthPage />),
                      },
                      {
                        path: '/platform/audit',
                        element: suspense(<Pages.PlatformAuditPage />),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // ── 404 ───────────────────────────────────────────────────────
      { path: '*', element: suspense(<Pages.NotFoundPage />) },
    ],
  },
]);

export default router;
